// SCR-STAFF-502 بخش «اقلام مصرفی» (AP-INV-02؛ US-CLN-041، BR-CLN-041): فرم پیش‌پر از BOM خدمت با مقدار استاندارد (AC-CLN-041.1)، انتخاب بچ
// الزامی برای کالای بچ‌دار با نمایش انقضا (AC-CLN-041.2؛ فقط بچ‌های معتبر به ترتیب FEFO)، رعایت «الزامی» و «قابل تغییر» BOM (AC-CAT-014.1).
// ثبت ← clinical.consumables.recorded.v1 و INV مصرف واقعی را به جای BOM کسر می‌کند (بدون کسر دوبل). فقط با feature.inv.inventory.
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type ClnConsumableItemView, type ClnServiceConsumablesView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, LoadingState, toJalali } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { fromMilli, toMilli } from '../inventory/stockCommon';

interface Row {
  itemId: string;
  itemName: string;
  unit: string;
  quantity: string;
  batchId: string;
  tracksBatch: boolean;
  isAdjustable: boolean;
  isMandatory: boolean;
  fromBom: boolean;
  batches: ClnConsumableItemView['batches'];
}

const consumablesKey = (tenantId: string | undefined, encounterId: string) => ['clinical', tenantId, 'encounter', encounterId, 'consumables'] as const;

function toRows(service: ClnServiceConsumablesView): Row[] {
  return service.items.map((item) => ({
    itemId: item.itemId,
    itemName: item.itemName ?? item.itemCode ?? item.itemId,
    unit: item.unit,
    quantity: String(fromMilli(item.quantityMilli)),
    batchId: item.batchId ?? '',
    tracksBatch: item.tracksBatch,
    isAdjustable: item.isAdjustable,
    isMandatory: item.isMandatory,
    fromBom: item.fromBom,
    batches: item.batches,
  }));
}

/** پنل اقلام مصرفی مواجهه؛ `editable` وقتی مواجهه لغو نشده و کاربر cln.encounter.manage دارد. */
export function ConsumablesPanel({ encounterId, editable }: { encounterId: string; editable: boolean }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const consumables = useQuery({
    queryKey: consumablesKey(session?.tenantId, encounterId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/encounters/{id}/consumables', { params: { path: { id: encounterId } } })),
  });
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-consumables" data-screen-id="SCR-STAFF-502-CONSUMABLES">
      <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
        {t('cln:consumables.title')}
      </Typography>
      {consumables.isPending && <LoadingState rows={2} />}
      {consumables.error && <ErrorState error={consumables.error} onRetry={() => void consumables.refetch()} compact />}
      {consumables.data && consumables.data.services.length === 0 && <Typography color="text.secondary">{t('cln:consumables.no_services')}</Typography>}
      <Stack spacing={2}>
        {consumables.data?.services.map((service) => <ServiceConsumables key={service.serviceRecordId} encounterId={encounterId} service={service} editable={editable} />)}
      </Stack>
    </Paper>
  );
}

function ServiceConsumables({ encounterId, service, editable }: { encounterId: string; service: ClnServiceConsumablesView; editable: boolean }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [rows, setRows] = useState<Row[]>(() => toRows(service));
  const [saved, setSaved] = useState(false);
  useEffect(() => setRows(toRows(service)), [service]);
  const canAddItems = editable && access.can('inv.item.view');
  const items = useQuery({
    queryKey: ['inventory', 'items', session?.tenantId, 'options'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/items', { params: { query: { active: true, limit: 100 } } })),
    enabled: canAddItems,
  });
  const update = (index: number, patch: Partial<Row>) => setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const missingBatch = rows.some((row) => row.tracksBatch && !row.batchId);
  const invalidQuantity = rows.some((row) => (toMilli(row.quantity) ?? -1) < 0);

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/clinical/encounters/{id}/consumables', {
          params: { path: { id: encounterId } },
          body: {
            serviceRecordId: service.serviceRecordId,
            items: rows.filter((row) => row.itemId).map((row) => ({ itemId: row.itemId, batchId: row.batchId || null, quantityMilli: toMilli(row.quantity) ?? 0, unit: row.unit })),
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSaved(false),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: consumablesKey(session?.tenantId, encounterId) });
    },
  });

  const addItem = (itemId: string) => {
    const item = items.data?.items.find((candidate) => candidate.id === itemId);
    if (!item || rows.some((row) => row.itemId === itemId)) {
      return;
    }
    setRows((current) => [
      ...current,
      { itemId, itemName: item.name, unit: item.consumptionUnit, quantity: '1', batchId: '', tracksBatch: item.tracksBatch, isAdjustable: true, isMandatory: false, fromBom: false, batches: [] },
    ]);
  };

  return (
    <Stack spacing={1} data-testid={`cln-consumables-${service.serviceRecordId}`}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('cln:consumables.service', { quantity: faDigits(String(service.quantity)) })}
        </Typography>
        <Chip size="small" color={service.recorded ? 'success' : 'default'} label={service.recorded ? t('cln:consumables.recorded') : t('cln:consumables.from_bom')} />
      </Stack>
      {rows.length === 0 && <Typography color="text.secondary">{t('cln:consumables.empty')}</Typography>}
      {rows.map((row, index) => (
        <Stack key={`${row.itemId}-${index}`} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
          <Typography sx={{ minWidth: 200 }}>
            {row.itemName}
            {row.isMandatory && ` · ${t('cln:consumables.mandatory')}`}
          </Typography>
          <TextField
            size="small"
            label={t('cln:consumables.quantity')}
            value={row.quantity}
            onChange={(event) => update(index, { quantity: event.target.value })}
            disabled={!editable || (row.fromBom && !row.isAdjustable)}
            sx={{ width: 160 }}
            slotProps={{ htmlInput: { inputMode: 'decimal', dir: 'ltr', 'data-testid': `consumable-quantity-${index}` }, input: { endAdornment: <Typography variant="caption">{t(`inv:unit.${row.unit}`, { defaultValue: row.unit })}</Typography> } }}
          />
          {row.tracksBatch && (
            <TextField
              select
              size="small"
              required
              label={t('cln:consumables.batch')}
              value={row.batchId}
              onChange={(event) => update(index, { batchId: event.target.value })}
              error={!row.batchId}
              helperText={!row.batchId ? t('cln:consumables.batch_required') : undefined}
              disabled={!editable}
              sx={{ minWidth: 240 }}
              slotProps={{ htmlInput: { 'data-testid': `consumable-batch-${index}` } }}
            >
              {row.batches.map((batch) => (
                <MenuItem key={batch.batchId} value={batch.batchId}>
                  <bdi dir="ltr">{batch.batchNumber}</bdi>
                  {batch.expiryDate ? ` · ${t('cln:consumables.expires', { date: toJalali(batch.expiryDate) })}` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          {editable && !(row.fromBom && row.isMandatory) && (
            <IconButton aria-label={t('cln:consumables.remove')} onClick={() => setRows((current) => current.filter((_row, i) => i !== index))} data-testid={`consumable-remove-${index}`}>
              <DeleteOutlineIcon />
            </IconButton>
          )}
        </Stack>
      ))}
      {editable && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          {canAddItems && (
            <TextField select size="small" label={t('cln:consumables.add')} value="" onChange={(event) => addItem(event.target.value)} sx={{ minWidth: 240 }} slotProps={{ htmlInput: { 'data-testid': 'consumable-add' } }}>
              {(items.data?.items ?? []).map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button variant="contained" disabled={missingBatch || invalidQuantity || save.isPending} onClick={() => save.mutate()} data-testid="consumables-save">
            {t('cln:consumables.save')}
          </Button>
        </Stack>
      )}
      {save.error && <ErrorState error={save.error} compact />}
      {saved && (
        <Alert severity="success" data-testid="consumables-saved">
          {t('cln:consumables.saved')}
        </Alert>
      )}
    </Stack>
  );
}
